import { format } from 'date-fns-tz';
import { DateFnsFormats, IDateTimeFormatter, TimeFormats } from '../date-formatter';
import { utcToZonedTime } from '../utils/date-fns-overrides.utils';
import { TIMEZONE_ALIASES } from '../../constants/time-zones.constant';


class DateTimeFormatter extends IDateTimeFormatter {
    static readonly FALLBACK_LOCALE = 'en-US';

    static readonly localTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
    static readonly localDateFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
    static readonly localTimeFormatOptions = { hour: 'numeric', minute: 'numeric' };
    static readonly localDateTimeFormatOptions = {
        ...DateTimeFormatter.localDateFormatOptions,
        ...DateTimeFormatter.localTimeFormatOptions
    } as Intl.DateTimeFormatOptions;

    /**
     * Return date shifted by provided TimeZone
     * @param {Date} date
     * @param {string} timezone
     * @return {Date}
     */
    static applyTimeZoneToDate(date: Date, timezone?: string): Date {
        const canonicalTimezone = timezone ? (TIMEZONE_ALIASES[timezone] || timezone) : DateTimeFormatter.localTimeZone;

        return timezone ? utcToZonedTime(date, canonicalTimezone) : date;
    }

    private readonly localeFormatter: Intl.DateTimeFormat;

    constructor() {
        super();
        /**
         * Create Intl.DateTimeFormat instance with system locale ('default')
         * @type {Intl.DateTimeFormat}
         */
        this.localeFormatter = new Intl.DateTimeFormat('default', DateTimeFormatter.localDateTimeFormatOptions);
    }

    public dateToFormattedString(date: Date, dateFormat: string, anyTimezone?: string): string {
        // by default format the date into the local timezone (which means conversion is not needed)
        // but at come point default will be changed to user-specific timezone
        const dateToRender = anyTimezone ? utcToZonedTime(date, anyTimezone) : date;

        const canonicalTimezone = anyTimezone ? (TIMEZONE_ALIASES[anyTimezone] || anyTimezone) : DateTimeFormatter.localTimeZone;

        return format(
          dateToRender,
          dateFormat,
          canonicalTimezone ? { timeZone: canonicalTimezone } : { timeZone: DateTimeFormatter.localTimeZone }
        );
    }

    public getLocalTimeFormat(): TimeFormats.Day12Hours | TimeFormats.Day24Hours {
        return this.localeFormatter.resolvedOptions().hour12 ? TimeFormats.Day12Hours : TimeFormats.Day24Hours;
    }

    public dateToLocalString(date: Date, timezone: string = DateTimeFormatter.localTimeZone): string {
        return this.localeFormatter.format(DateTimeFormatter.applyTimeZoneToDate(date, timezone));
    }

    public dateToLocalStringFormat(date: Date, timezone: string = DateTimeFormatter.localTimeZone, dateFormat: TimeFormats): string {
        const dateFnsFormat = dateFormat === TimeFormats.Day12Hours
            ? DateFnsFormats.ShortDayMonthYear12Hours
            : DateFnsFormats.ShortDayMonthYear24Hours;
        return format(DateTimeFormatter.applyTimeZoneToDate(date, timezone), dateFnsFormat);
    }
}

export { DateTimeFormatter };
